import { useEffect } from 'react'

import styled from '@emotion/styled'

import { breakpoints, Flex, Hug, maxWidths, spacing, Stack, Text } from '@cash-design-system/react'
import { StatusResult, StatusResultAction } from '@cash-web/protos/squareup/franklin/status_result.pb'
import logger from '@cash-web/shared/util-logger'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useLoadUrl } from '@cash-web/shared-feature-use-load-url'
import { WebViewViewComponent } from '@squareup/customer-data-events-web'

import { BlockerLoading } from '../BlockerLoading/BlockerLoading'
import { BlockerStatusResultIcon } from '../BlockerStatusResultIcon/BlockerStatusResultIcon'
import { TextElement } from '../FormBlocker/elements'
import { BlockerStatusResultButton } from './BlockerStatusResultButton'

async function redirect(
  action: StatusResultAction,
  redirectPath: string | undefined,
  loadUrl: ReturnType<typeof useLoadUrl>
) {
  if (redirectPath == null) {
    logger.warn(new Error(`Missing redirect_uri for StatusResult -> action:${action}`))
    return
  }

  await loadUrl({ url: redirectPath, target: '_self' })
}

const LoadingModalContainer = styled.div`
  /* 4rem (header) + 2rem (modal padding), doubled so the container centerpoint is the middle of the screen */
  height: ${({ theme }) => `calc(100dvh - (2 * (${theme.spacing.headerSpacing} + ${theme.spacing.xl})))`};

  ${breakpoints.tablet} {
    height: 450px;
  }
`

export const BlockerStatusResult = ({ result, onClose }: { result: StatusResult; onClose?: () => void }) => {
  const { text, icon, primary_button, secondary_button, action: originalAction, promo_text } = result || {}
  const shouldBeDisplayedInDialog = text != null && icon != null && (primary_button != null || secondary_button != null)
  // Sometimes action isn't set,
  const action = originalAction == null && shouldBeDisplayedInDialog ? 'NO_ACTION_SHOW_DIALOG' : originalAction
  const logCdfEvent = useLogCashWebCdfEvent()
  const loadUrl = useLoadUrl()
  useEffect(() => {
    logCdfEvent(
      WebViewViewComponent({
        componentName: 'BlockerStatusResult',
        componentType: `Action[${action}] Icon[${icon}]`,
      })
    )
    logger.addBreadcrumb('BlockerStatusResult', { action, icon })
  }, [logCdfEvent, action, icon])

  const shouldRedirect =
    action === 'INSTANT_BACK_NAVIGATION' ||
    action === 'START_SUPPORT_FLOW' ||
    action === 'INSTANT_INTERNAL_REDIRECT' ||
    action === 'INSTANT_REDIRECT'

  // Redirect must happen in a useEffect, not during render. Calling loadUrl during render
  // triggers setState on a parent (JourneyTargetRenderer), which causes an infinite re-render loop.
  useEffect(() => {
    if (shouldRedirect) {
      redirect(action, result?.redirect_uri, loadUrl)
    }
  }, [shouldRedirect, action, result?.redirect_uri, loadUrl])

  // Only handles UI — redirect side effects are in the useEffect above
  switch (action) {
    case 'NO_ACTION_SHOW_DIALOG':
    case 'SHOW_DIALOG': {
      return (
        <Hug maxWidth={maxWidths.content} fullWidth={true}>
          <Stack space={spacing.headerSpacing}>
            <Stack space={spacing.m}>
              <Stack space={spacing.l}>
                <BlockerStatusResultIcon name={icon} width="3rem" statusText={text} />
                <Text typography="numeralSmall" as="h2" color="textStandard" overflowWrap="anywhere">
                  {text}
                </Text>
                {promo_text && <TextElement text={promo_text} />}
              </Stack>
            </Stack>
            <Flex gap={spacing.l}>
              <BlockerStatusResultButton isPrimary={false} button={secondary_button} onClose={onClose} />
              <BlockerStatusResultButton isPrimary={true} button={primary_button} onClose={onClose} />
            </Flex>
          </Stack>
        </Hug>
      )
    }
    case 'INSTANT_BACK_NAVIGATION': {
      // show a loading spinner because the redirect URL for back navigation is always external
      return (
        <LoadingModalContainer>
          <BlockerLoading height={'100%'} />
        </LoadingModalContainer>
      )
    }
    case 'START_SUPPORT_FLOW': // this is deprecated but works the same as INSTANT_INTERNAL_REDIRECT
    case 'INSTANT_INTERNAL_REDIRECT':
    case 'INSTANT_REDIRECT': {
      break
    }
    case 'END_ACTIVITY': // currently only supported for Android OS, but adding it here for completeness
    default: {
      logger.error(new Error(`Unsupported Blocker StatusResult -> action: ${action} -> icon: ${icon}`))
    }
  }
  return null
}
